/**
 * What Karr says about a client whose GST portal session has died (noemailtick,
 * gsturgent). Pure: the moving line (GstDisconnectTicker), the office's still
 * line and the fill rail's Urgent rows all read their words from here.
 *
 * Founder, 26 Sep 2026: a moving line of every client's name cannot be read by an
 * office with twenty of them. So the office sees a COUNT that does not move, and a
 * press opens the Urgent rows in the fill rail, one per client, each with its own
 * button. A business's own people keep the moving line: it only ever holds their
 * own one or two businesses, and says "you", never "the client".
 */

export interface DisconnectedClient {
    org_id: string;
    org_name: string | null;
    gstin: string;
    has_client_email: boolean;
    since: string | null;
    /**
     * boardfix (6 Oct 2026). 'expired': a saved session ran out (the only kind
     * there was). 'link_failed': the month run tried to send the reconnect link
     * and it did not go. 'never_connected': Karr has never been connected to
     * this GSTIN, so a reconnect link has no username to go with. Absent until
     * migration 20261006 boardfix is applied, and then read as 'expired'.
     */
    kind?: 'expired' | 'link_failed' | 'never_connected' | null;
    /** The run's own sentence for a link that did not go. */
    reason?: string | null;
}

/** The row's kind, an old database's rows read as the one kind it knew. */
export const kindOf = (c: DisconnectedClient): NonNullable<DisconnectedClient['kind']> => c.kind ?? 'expired';

export const clientName = (c: DisconnectedClient): string => (c.org_name ?? '').trim() || c.gstin;

/** One item on the moving line, for the business's own people: about their own portal. */
export function ownLineItem(c: DisconnectedClient): { text: string; to: string; action: string } {
    const name = clientName(c);
    return c.has_client_email
        ? {
            text: `${name} (${c.gstin}): your GST portal connection has stopped. We emailed you the link to reconnect.`,
            to: '/settings#tax', action: 'Reconnect now',
        }
        : {
            text: `${name} (${c.gstin}): your GST portal connection has stopped, and no email is saved to send you the link.`,
            to: '/settings#org', action: 'Add your email',
        };
}

/**
 * The office's line: a count that stays still, never a stream of names. Counted
 * by client, not by GSTIN, because a client with two registrations is still one
 * phone call. `urgent` when any of them has no email, since nobody was told.
 */
export function officeSummary(cs: readonly DisconnectedClient[]): { text: string; urgent: boolean } {
    const orgs = new Map<string, DisconnectedClient[]>();
    for (const c of cs) orgs.set(c.org_id, [...(orgs.get(c.org_id) ?? []), c]);
    const noEmail = [...orgs.values()].filter(rows => rows.some(r => !r.has_client_email)).length;
    if (orgs.size === 1) {
        const [first] = cs;
        return {
            text: `GST portal disconnected for ${clientName(first)}${noEmail ? ', and no email is saved to tell them' : ''}`,
            urgent: noEmail > 0,
        };
    }
    return {
        text: `GST portal disconnected for ${orgs.size} clients${noEmail ? `, ${noEmail} with no email saved, so nobody told them` : ''}`,
        urgent: noEmail > 0,
    };
}

/**
 * The fill rail row, split for the eye: the business on one line, what is wrong
 * with it as a short tag under it. The flat `railRowTitle` stays for places that
 * need one string (a ticked-off row, a screen reader).
 */
export function railRowParts(c: DisconnectedClient, office: boolean): { name: string; status: string; noEmail: boolean } {
    const name = clientName(c);
    if (!office) return { name: `Reconnect ${name}'s GST portal`, status: '', noEmail: !c.has_client_email };
    const kind = kindOf(c);
    if (kind === 'never_connected') return { name, status: 'Portal never connected', noEmail: !c.has_client_email };
    if (kind === 'link_failed') return { name, status: c.has_client_email ? 'Reconnect link did not go' : 'Link did not go, no email', noEmail: !c.has_client_email };
    return { name, status: c.has_client_email ? 'Portal disconnected' : 'Portal disconnected, no email', noEmail: !c.has_client_email };
}

/** The fill rail row's title. */
export function railRowTitle(c: DisconnectedClient, office: boolean): string {
    const name = clientName(c);
    if (!office) return `Reconnect ${name}'s GST portal`;
    const kind = kindOf(c);
    if (kind === 'never_connected') return `${name}: GST portal never connected`;
    if (kind === 'link_failed') return `${name}: reconnect link did not go`;
    return c.has_client_email ? `${name}: GST portal disconnected` : `${name}: GST portal disconnected, no email`;
}
